// The customer-side money pages: balance, orders, invoices,
// returns, licenses, transactions and balance reloads.
// Merged onto the web-dashboard prototype (see dashboard.js);
// `this` is the component instance.
export default {

    async loadBalance() {
      // Page loads, the renewal dialog and post-payment refreshes may overlap.
      // Only the latest read can replace the displayed balance.
      const seq = this._balanceSeq = (this._balanceSeq || 0) + 1;
      const session = sessionStorage.getItem('session');
      try {
        const response = await this.apiFetch(`${this.apiUrl('billing/balance')}`);
        if (response.ok) {
          const data = await response.json();
          if (seq !== this._balanceSeq || session !== sessionStorage.getItem('session')) return false;
          this.balance = data.balance || 0;
          return true;
        }
      } catch (error) {}
      return false;
    },

    // A gateway-credited reload (Stripe checkout, and every subscription's
    // first cycle) is applied by a webhook that lands moments after the
    // redirect back here — or minutes later when the gateway retries a
    // delivery. One balance read at page load loses that race, so after a
    // gateway return the card re-reads itself until the credit shows up
    // (bounded, and abandoned on leaving the billing page).
    async watchBalance() {
      if (this._watchingBalance) return;
      this._watchingBalance = true;
      const before = this.balance;
      try {
        for (let tries = 0; tries < 40; tries++) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          if (this.page !== 'billing') return;
          await this.loadBalance();
          if (this.balance !== before) {
            // The credit landed; the new subscription's status row may
            // have moved with it (pending → active).
            await this.loadSubscriptions();
            this.update();
            return;
          }
        }
      } finally {
        this._watchingBalance = false;
      }
    },

    // Fetches the stored invoice PDF and triggers a browser download.
    // Fetch-as-blob because the API needs the Authorization header, which a
    // plain link can't carry. scope 'admin' uses the any-order admin
    // endpoint; default is the signed-in user's own orders.
    // Fetches the order's invoice PDF (Bearer-authorized, so it can't be a
    // plain link) and returns a blob: URL, or null after toasting the failure.
    async invoiceBlobUrl(order, scope) {
      const endpoint = scope === 'admin' ? 'admin/billing/invoice' : 'billing/invoice';
      const response = await this.apiFetch(this.apiUrl(endpoint + '?order_id=' + encodeURIComponent(order.id)));
      if (!response.ok) {
        const msg = await response.json();
        this.showToast(msg, 'danger');
        return null;
      }
      return URL.createObjectURL(await response.blob());
    },

    async downloadInvoice(order, scope) {
      try {
        const url = await this.invoiceBlobUrl(order, scope);
        if (!url) return;
        const a = document.createElement('a');
        a.href = url;
        a.download = 'invoice-' + String(order.invoice_no).padStart(6, '0') + '.pdf';
        a.click();
        // Defer the revoke: revoking synchronously after click() can race the
        // download navigation in Safari and yield an empty/failed download.
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    // Opens the invoice PDF in a new tab. The tab must be opened synchronously
    // inside the click gesture (popup blockers), then pointed at the blob URL
    // once the authorized fetch lands.
    async viewInvoice(order, scope) {
      const win = window.open('', '_blank');
      try {
        const url = await this.invoiceBlobUrl(order, scope);
        if (!url) {
          if (win) win.close();
          return;
        }
        if (win) win.location = url;
        else window.open(url, '_blank'); // popup was blocked; try direct
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        if (win) win.close();
        this.showToast(e.message, 'danger');
      }
    },

    // Re-emails the order's receipt — license keys and invoice PDF — for
    // receipts lost to spam filters or mailbox trouble. scope 'admin' works
    // on any customer's order; default is the signed-in user's own orders,
    // always to their account email.
    async resendReceipt(order, scope) {
      const admin = scope === 'admin';
      const confirmed = await this.showConfirm({
        title: 'Resend Receipt',
        message: 'Email the license keys and invoice for order ' + order.id.slice(0, 8) +
          ' to ' + (admin ? order.user_email : 'your account email') + '?',
        action: 'Send',
      });
      if (!confirmed) return;
      const endpoint = admin ? 'admin/order/resend-receipt' : 'billing/order/resend-receipt';
      try {
        const response = await this.apiFetch(`${this.apiUrl(endpoint)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_id: order.id }),
        });
        const msg = await response.json();
        this.showToast(msg, response.ok ? 'success' : 'danger');
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    orderItemsSummary(items) {
      if (!items || !items.length) return '';
      return items.map(function(i) {
        return i.product_name + (i.quantity > 1 ? ' x' + i.quantity : '');
      }).join(', ');
    },

    async loadUserOrders() {
      try {
        if (await this.fetchList('userOrders', `${this.apiUrl('billing/orders')}`)) {
          this.userOrdersLoaded = true;
        }
      } catch (error) {}
    },

    async loadMyLicenses() {
      try {
        if (await this.fetchList('myLicenses', `${this.apiUrl('licenses')}`)) {
          this.myLicensesLoaded = true;
        }
      } catch (error) {}
    },

    // "2 of 3": how many devices a key holds, of how many it may.
    licenseDeviceCount(lic) {
      return (lic.device_count || 0) + ' of ' + (lic.max_devices || 1);
    },

    // The Registered Devices dialog, over the row the list holds. source:
    // 'admin' from the store Licenses page (full serials, the system each
    // runs, last address), 'user' from My Licenses (masked hints).
    openLicenseDevices(lic, source) {
      this.licenseDevicesLicense = lic;
      this.licenseDevicesSource = source;
      this.update();
      this.querySelector('.license-devices-dialog').showModal();
    },

    closeLicenseDevices() {
      this.querySelector('.license-devices-dialog').close();
      this.licenseDevicesLicense = { devices: [] };
      this.update();
    },

    // Called by fetchList whenever a list's answer is applied. An open
    // Devices dialog follows its key in the list it was opened from (a
    // release, an edit, a search landing late); a key no longer listed
    // closes it.
    listLoaded(name) {
      if (name !== (this.licenseDevicesSource === 'admin' ? 'licenses' : 'myLicenses')) return;
      const dialog = this.querySelector('.license-devices-dialog');
      if (!dialog || !dialog.open) return;
      const row = (this[name] || []).find((lic) => lic.license_key === this.licenseDevicesLicense.license_key);
      if (!row) return this.closeLicenseDevices();
      this.licenseDevicesLicense = row;
      this.update();
    },

    licenseRenewalPeriod(lic) {
      const unit = lic.renew_unit || 'day';
      const period = Number(lic.renew_period || 0);
      return period + ' ' + unit + (period === 1 ? '' : 's');
    },

    licenseRenewalTerms(lic) {
      if (!lic.end_date || !(lic.renew_period > 0) || !(lic.renew_price > 0)) return 'Not available';
      return this.formatAmount(lic.renew_price) + ' / ' + this.licenseRenewalPeriod(lic);
    },

    canRenewLicense(lic) {
      return !!lic.end_date && lic.renew_period > 0 && lic.renew_price > 0 && lic.status !== 'revoked' && !this.hasPendingLicenseRenewal(lic);
    },

    hasPendingLicenseRenewal(lic) {
      return this.licenseRenewals.some((payment) => payment.license_key === lic.license_key);
    },

    licenseRenewSessionCurrent(session) {
      return !!session && session === sessionStorage.getItem('session');
    },

    licenseRenewTitle(lic) {
      if (this.hasPendingLicenseRenewal(lic)) return 'Continue or cancel the pending renewal payment shown above';
      if (lic.status === 'revoked') return 'Revoked licenses cannot be renewed';
      if (!lic.end_date) return 'Lifetime licenses do not need renewal';
      if (!this.canRenewLicense(lic)) return 'Renewal is not available for this license';
      return 'Renew this license with your balance, an online payment, or both';
    },

    renewalEndDate() {
      const lic = this.renewalLicense;
      if (!lic.end_date || !(lic.renew_period > 0)) return '';
      const date = new Date(Math.max(Date.now(), new Date(lic.end_date).getTime()));
      const period = Number(lic.renew_period);
      const unit = lic.renew_unit || 'day';
      if (unit === 'month' || unit === 'year') {
        // Calendar periods keep the same day where it exists, and clamp to
        // the last day otherwise (January 31 -> February 28/29).
        const day = date.getUTCDate();
        date.setUTCDate(1);
        if (unit === 'month') date.setUTCMonth(date.getUTCMonth() + period);
        else date.setUTCFullYear(date.getUTCFullYear() + period);
        const lastDay = new Date(date);
        lastDay.setUTCMonth(lastDay.getUTCMonth() + 1, 0);
        date.setUTCDate(Math.min(day, lastDay.getUTCDate()));
      } else {
        date.setUTCDate(date.getUTCDate() + period);
      }
      return date.toISOString();
    },

    // What the renewal costs: the license's renewal price, or the promo
    // code's price once the store has priced it.
    licenseRenewPrice() {
      return this.licenseRenewPromo ? Number(this.licenseRenewPromo.price) : Number(this.renewalLicense.renew_price || 0);
    },

    licenseRenewShortfall() {
      return Math.max(0, Math.round((this.licenseRenewPrice() - this.balance) * 100) / 100);
    },

    licenseRenewBalanceMax() {
      return Math.max(0, Math.floor(Math.min(this.balance, this.licenseRenewPrice()) * 100 + 0.0000001) / 100);
    },

    // Changes the price the dialog splits (`change` sets or drops the code)
    // and keeps the owner's choice of balance share: the whole price stays
    // the whole price, and any other share is held to what the new price
    // allows.
    repriceLicenseRenew(change) {
      const whole = Number(this.licenseRenewBalanceAmount) === this.licenseRenewBalanceMax();
      change();
      if (!this.licenseBalanceLoaded) return;
      const max = this.licenseRenewBalanceMax();
      this.licenseRenewBalanceAmount = whole ? max : Math.min(Number(this.licenseRenewBalanceAmount) || 0, max);
    },

    // Asks the store what the typed code is worth on this license's
    // renewal, and keeps the answer for the split and the request.
    async applyLicenseRenewPromo() {
      if (this.licenseRenewPromoBusy || this.licenseRenewLoading) return;
      const code = (this.licenseRenewPromoCode || '').trim();
      if (!code) {
        this.repriceLicenseRenew(() => { this.licenseRenewPromo = null; });
        this.licenseRenewPromoNote = '';
        this.update();
        return;
      }
      const session = sessionStorage.getItem('session');
      const selected = this.renewalLicense;
      this.licenseRenewPromoBusy = true;
      this.update();
      try {
        const response = await this.apiFetch(this.apiUrl('license/renewal/promo'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ license_key: selected.license_key, code }),
        });
        const data = await response.json();
        if (selected !== this.renewalLicense || !this.licenseRenewSessionCurrent(session)) return;
        if (response.ok && data && typeof data === 'object') {
          this.repriceLicenseRenew(() => {
            this.licenseRenewPromo = { code: data.code, price: Number(data.discounted_unit_price) || 0, discount: Number(data.discount) || 0 };
          });
          this.licenseRenewPromoCode = data.code;
          this.licenseRenewPromoNote = data.code + ' applied: ' + data.words + '.';
          this.licenseRenewPromoRefused = false;
        } else {
          this.repriceLicenseRenew(() => { this.licenseRenewPromo = null; });
          this.licenseRenewPromoNote = typeof data === 'string' && data ? data : 'The promo code could not be checked. Please try again.';
          this.licenseRenewPromoRefused = true;
        }
      } catch (e) {
        if (e.sessionExpired) return;
        this.repriceLicenseRenew(() => { this.licenseRenewPromo = null; });
        this.licenseRenewPromoNote = 'The promo code could not be checked. Please try again.';
        this.licenseRenewPromoRefused = true;
      } finally {
        this.licenseRenewPromoBusy = false;
        this.update();
      }
    },

    // Whether `typed` is the applied code. A code is one code in any case,
    // and the store answers with the spelling staff gave it, which need not
    // be the buyer's: compared as typed, ourcoders.com would never match
    // OURCODERS.COM and the dialog would apply it again on every press.
    sameLicenseRenewPromo(typed) {
      return !!this.licenseRenewPromo && (typed || '').trim().toUpperCase() === this.licenseRenewPromo.code.toUpperCase();
    },

    // A code changed after it was applied is not applied until Apply is pressed again.
    licenseRenewPromoTyped() {
      if (this.licenseRenewPromo && !this.sameLicenseRenewPromo(this.licenseRenewPromoCode)) {
        this.repriceLicenseRenew(() => { this.licenseRenewPromo = null; });
        this.licenseRenewPromoNote = '';
        this.update();
      }
    },

    licenseRenewPromoKey(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.applyLicenseRenewPromo();
      }
    },

    licenseRenewBalanceValid() {
      const amount = this.licenseRenewBalanceAmount;
      return amount !== null && amount !== '' && Number.isFinite(Number(amount)) && amount >= 0 && amount <= this.licenseRenewBalanceMax() && Number(amount) === Math.round(Number(amount) * 100) / 100;
    },

    licenseRenewGatewayAmount() {
      return Math.max(0, Math.round((this.licenseRenewPrice() - Number(this.licenseRenewBalanceAmount || 0)) * 100) / 100);
    },

    licenseRenewReady() {
      return this.licenseBalanceLoaded && this.canRenewLicense(this.renewalLicense) && this.licenseRenewBalanceValid() &&
        (this.licenseRenewGatewayAmount() === 0 || (this.licenseRenewMethodsLoaded && this.licenseRenewMethods.includes(this.licenseRenewGateway)));
    },

    async openLicenseRenew(lic) {
      if (this.licenseRenewLoading || !this.canRenewLicense(lic)) return;
      const session = sessionStorage.getItem('session');
      if (!session) return;
      // Keep the displayed terms as a snapshot. They accompany the renewal
      // request so a concurrent renewal or price edit cannot charge unseen terms.
      const selected = this.renewalLicense = { ...lic };
      this.licenseBalanceLoaded = false;
      this.licenseBalanceLoading = true;
      this.licenseRenewMethodsLoaded = false;
      this.licenseRenewMethodsLoading = true;
      this.licenseRenewBalanceAmount = 0;
      this.licenseRenewPromo = null;
      this.licenseRenewPromoCode = '';
      this.licenseRenewPromoNote = '';
      this.licenseRenewPromoRefused = false;
      this.licenseRenewPromoBusy = false;
      this.update();
      this.querySelector('.license-renew-dialog').showModal();
      await Promise.all([
        this.loadBalance().then((loaded) => {
          if (selected !== this.renewalLicense || !this.licenseRenewSessionCurrent(session)) return;
          this.licenseBalanceLoaded = loaded;
          this.licenseBalanceLoading = false;
          this.licenseRenewBalanceAmount = this.licenseRenewBalanceMax();
          this.update();
        }),
        this.loadPaymentMethods().then((loaded) => {
          if (selected !== this.renewalLicense || !this.licenseRenewSessionCurrent(session)) return;
          this.licenseRenewMethodsLoaded = loaded;
          this.licenseRenewMethodsLoading = false;
          this.update();
        }),
      ]);
    },

    closeLicenseRenew() {
      if (!this.licenseRenewLoading) this.querySelector('.license-renew-dialog').close();
    },

    cancelLicenseRenew(event) {
      if (this.licenseRenewLoading) event.preventDefault();
    },

    async renewLicense() {
      if (this.licenseRenewLoading || !this.licenseRenewReady()) return;
      // A code typed and not yet applied is applied, and the renewal waits
      // for another press: this dialog takes the balance with no step
      // after it, so the owner sees the price with the code before paying it.
      const typed = (this.licenseRenewPromoCode || '').trim();
      if (typed && !this.sameLicenseRenewPromo(typed)) {
        await this.applyLicenseRenewPromo();
        return;
      }
      const session = sessionStorage.getItem('session');
      if (!session) return;
      const attempt = this._licenseRenewAttempt = {};
      this.licenseRenewLoading = true;
      this.update();
      const lic = this.renewalLicense;
      try {
        const response = await this.apiFetch(this.apiUrl('license/renew'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            license_key: lic.license_key, end_date: lic.end_date,
            renew_period: lic.renew_period, renew_unit: lic.renew_unit || 'day', renew_price: lic.renew_price,
            balance_amount: Number(this.licenseRenewBalanceAmount),
            ...(this.licenseRenewPromo ? { promo_code: this.licenseRenewPromo.code } : {}),
            ...(this.licenseRenewGatewayAmount() > 0 ? { payment_method: this.licenseRenewGateway } : {}),
          }),
        });
        const data = await response.json();
        if (this._licenseRenewAttempt !== attempt || !this.licenseRenewSessionCurrent(session)) return;
        if (response.ok) {
          this.querySelector('.license-renew-dialog').close();
          if (data.id && data.status) {
            await this.applyLicenseRenewal(data);
            if (this._licenseRenewAttempt !== attempt || !this.licenseRenewSessionCurrent(session)) return;
            if (data.status === 'pending') {
              if (data.url) window.location.href = data.url;
              else this.watchLicenseRenewals();
            }
          } else {
            this.balance = data.balance;
            this.myLicenses = this.myLicenses.map((row) => row.license_key === lic.license_key ? { ...row, ...data.license } : row);
            this.showToast('License renewed for ' + this.formatAmount(data.charged));
            await Promise.all([this.loadMyLicenses(), this.loadBalance(), this.loadTransactions()]);
          }
        } else {
          this.showToast(data.message || data, 'danger');
          // A stale end date or edited terms need a fresh confirmation.
          if (response.status === 409) this.querySelector('.license-renew-dialog').close();
          if (data.renewal) {
            await this.applyLicenseRenewal(data.renewal);
            this.watchLicenseRenewals();
          }
          this.licenseBalanceLoaded = await this.loadBalance();
          await this.loadMyLicenses();
        }
      } catch (e) {
        if (this._licenseRenewAttempt === attempt && this.licenseRenewSessionCurrent(session) && !e.sessionExpired) this.showToast(e.message, 'danger');
      } finally {
        if (this._licenseRenewAttempt === attempt && this.licenseRenewSessionCurrent(session)) {
          this.licenseRenewLoading = false;
          this.update();
        }
      }
    },

    async loadLicenseRenewals() {
      const session = sessionStorage.getItem('session');
      if (!session) return;
      const seq = this._licenseRenewalsSeq = (this._licenseRenewalsSeq || 0) + 1;
      try {
        const response = await this.apiFetch(this.apiUrl('license/renewals'));
        const data = await response.json();
        if (seq !== this._licenseRenewalsSeq || !this.licenseRenewSessionCurrent(session)) return;
        if (response.ok) {
          this.licenseRenewals = Array.isArray(data) ? data : [];
        } else {
          this.showToast(data.message || data, 'danger');
        }
      } catch (error) {
        if (seq === this._licenseRenewalsSeq && this.licenseRenewSessionCurrent(session) && !error.sessionExpired) this.showToast('Could not load renewal payments. Please refresh.', 'danger');
      }
      if (this.licenseRenewSessionCurrent(session)) this.update();
    },

    resumeLicenseRenewal(event) {
      if (this.licenseRenewalActionID || !sessionStorage.getItem('session')) event.preventDefault();
    },

    // Apply the authoritative checkout state. A terminal response invalidates
    // older list/status reads so they cannot restore an already-finished payment.
    async applyLicenseRenewal(payment, notify = true) {
      if (!payment.id) return;
      const session = sessionStorage.getItem('session');
      if (!session) return;
      this._licenseRenewalsSeq = (this._licenseRenewalsSeq || 0) + 1;
      const requests = this._licenseRenewalRequests ??= {};
      requests[payment.id] = (requests[payment.id] || 0) + 1;
      const existing = this.licenseRenewals.find((row) => row.id === payment.id);
      if (payment.status === 'pending') {
        this.licenseRenewals = existing
          ? this.licenseRenewals.map((row) => row.id === payment.id ? { ...row, ...payment } : row)
          : [...this.licenseRenewals, payment];
      } else {
        this.licenseRenewals = this.licenseRenewals.filter((row) => row.id !== payment.id);
        if (notify) {
          const fallback = {
            completed: 'License renewed successfully',
            credited: 'Your payment was added to your balance. Review the license terms and renew again.',
            canceled: 'Renewal payment canceled',
            failed: 'Renewal payment could not be completed',
          };
          this.showToast(payment.message || fallback[payment.status] || 'Renewal payment updated', payment.status === 'failed' ? 'danger' : 'success');
        }
        if (payment.status === 'completed' || payment.status === 'credited') {
          await Promise.all([this.loadMyLicenses(), this.loadBalance(), this.loadTransactions()]);
        }
      }
      if (this.licenseRenewSessionCurrent(session)) this.update();
    },

    async checkLicenseRenewal(id, silent = false) {
      const session = sessionStorage.getItem('session');
      if (!session) return;
      const requests = this._licenseRenewalRequests ??= {};
      const seq = requests[id] = (requests[id] || 0) + 1;
      if (!silent) { this.licenseRenewalActionID = id; this.update(); }
      try {
        const response = await this.apiFetch(this.apiUrl('license/renewal?id=' + encodeURIComponent(id)));
        const data = await response.json();
        if (requests !== this._licenseRenewalRequests || seq !== requests[id] || !this.licenseRenewSessionCurrent(session)) return;
        if (response.ok) {
          await this.applyLicenseRenewal(data);
          if (!silent && data.status === 'pending' && this.licenseRenewSessionCurrent(session)) this.showToast('Payment has not completed yet. Continue payment or check again shortly.');
        } else if (!silent) {
          this.showToast(data.message || data, 'danger');
        }
      } catch (error) {
        if (!silent && this.licenseRenewSessionCurrent(session) && !error.sessionExpired) this.showToast(error.message, 'danger');
      } finally {
        if (this.licenseRenewSessionCurrent(session)) {
          if (!silent && this.licenseRenewalActionID === id) this.licenseRenewalActionID = '';
          this.update();
        }
      }
    },

    async cancelLicenseRenewal(id) {
      if (this.licenseRenewalActionID) return;
      const session = sessionStorage.getItem('session');
      if (!session) return;
      const requests = this._licenseRenewalRequests ??= {};
      this.licenseRenewalActionID = id;
      this.update();
      try {
        const response = await this.apiFetch(this.apiUrl('license/renewal/cancel'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }),
        });
        const data = await response.json();
        if (requests !== this._licenseRenewalRequests || !this.licenseRenewSessionCurrent(session)) return;
        if (response.ok) await this.applyLicenseRenewal(data);
        else this.showToast(data.message || data, 'danger');
      } catch (error) {
        if (this.licenseRenewSessionCurrent(session) && !error.sessionExpired) this.showToast(error.message, 'danger');
      } finally {
        if (requests === this._licenseRenewalRequests && this.licenseRenewSessionCurrent(session)) {
          this.licenseRenewalActionID = '';
          this.update();
        }
      }
    },

    async handleLicenseRenewalReturn(result) {
      const session = sessionStorage.getItem('session');
      if (!session) return;
      if (result.result === 'cancel') {
        await this.cancelLicenseRenewal(result.id);
        return;
      }
      if (result.result !== 'paypal_capture') {
        await this.checkLicenseRenewal(result.id);
        return;
      }
      if (!result.orderID) {
        this.showToast('The PayPal return is missing its payment reference. Continue the pending payment to try again.', 'danger');
        return;
      }
      const requests = this._licenseRenewalRequests ??= {};
      this.licenseRenewalActionID = result.id;
      this.update();
      try {
        const response = await this.apiFetch(this.apiUrl('license/renewal/paypal-capture'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: result.id, order_id: result.orderID }),
        });
        const data = await response.json();
        if (requests !== this._licenseRenewalRequests || !this.licenseRenewSessionCurrent(session)) return;
        if (response.ok) await this.applyLicenseRenewal(data);
        else {
          this.showToast(data.message || data, 'danger');
          await this.checkLicenseRenewal(result.id, true);
        }
      } catch (error) {
        if (this.licenseRenewSessionCurrent(session) && !error.sessionExpired) this.showToast(error.message, 'danger');
      } finally {
        if (requests === this._licenseRenewalRequests && this.licenseRenewSessionCurrent(session)) {
          this.licenseRenewalActionID = '';
          this.update();
        }
      }
    },

    async watchLicenseRenewals() {
      if (this._licenseRenewalWatcher || !this.licenseRenewals.length) return;
      const session = sessionStorage.getItem('session');
      if (!session) return;
      const watcher = this._licenseRenewalWatcher = {};
      try {
        for (let tries = 0; tries < 40; tries++) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          if (this._licenseRenewalWatcher !== watcher || !this.licenseRenewSessionCurrent(session) || this.page !== 'my-licenses' || !this.licenseRenewals.length) return;
          await Promise.all(this.licenseRenewals.map((payment) => this.checkLicenseRenewal(payment.id, true)));
        }
      } finally {
        if (this._licenseRenewalWatcher === watcher) this._licenseRenewalWatcher = null;
      }
    },

    async copyLicenseKey(key) {
      try {
        await navigator.clipboard.writeText(key);
        this.showToast('Key copied to clipboard');
      } catch (e) {
        this.showToast('Could not copy — select the key manually', 'danger');
      }
    },

    viewLicenseKey(lic) {
      this.viewedLicenseKey = lic.license_key;
      this.update();
      this.querySelector('.license-key-dialog').showModal();
    },

    closeLicenseKeyDialog() {
      this.querySelector('.license-key-dialog').close();
    },

    async loadTransactions() {
      try {
        if (await this.fetchList('transactions', `${this.apiUrl('billing/transactions')}`)) {
          this.transactionsLoaded = true;
        }
      } catch (error) {}
    },

    openReturnDialog(order) {
      this.returnOrderID = order.id;
      this.returnRefundTo = 'gateway';
      this.returnItems = (order.items || []).filter(function(i) {
        return i.quantity - i.returned > 0;
      }).map(function(i) {
        return {
          id: i.id,
          product_name: i.product_name,
          price: i.price,
          quantity: i.quantity,
          returned: i.returned,
          returnQty: i.quantity - i.returned,
        };
      });
      this.update();
      this.querySelector('.return-dialog').showModal();
    },

    closeReturnDialog() {
      this.querySelector('.return-dialog').close();
    },

    async submitReturn() {
      const items = this.returnItems.filter(function(i) {
        return parseInt(i.returnQty) > 0;
      }).map(function(i) {
        return { order_item_id: i.id, quantity: parseInt(i.returnQty) };
      });
      if (items.length === 0) {
        this.showToast('Select at least one item to return', 'danger');
        return;
      }
      this.returnLoading = true;
      this.update();

      // User submits a request; admin processes directly
      const isAdmin = this.orderDetailSource === 'admin';
      const url = isAdmin ? `${this.apiUrl('admin/return-request/direct')}` : `${this.apiUrl('billing/return-request')}`;
      const body = { order_id: this.returnOrderID, items: items };
      if (isAdmin) {
        body.refund_to = this.returnRefundTo;
      }

      try {
        const response = await this.apiFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (response.ok) {
          this.closeReturnDialog();
          this.showToast(isAdmin ? data.message || data : 'Return request submitted');
          if (isAdmin) {
            await this.loadOrders();
          } else {
            await this.loadUserOrders();
          }
        } else {
          this.showToast(data, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      } finally {
        this.returnLoading = false;
      }
    },

    // The recurring-reload plans on offer, defined in the gateways' own
    // dashboards and read back through the server. No plans (none defined,
    // or gateways unconfigured) hides the whole Subscribe block. Each
    // gateway's plans stay in their own picker: a Payment Method select
    // first, then only that gateway's plans.
    async loadSubscriptionPlans() {
      try {
        const response = await this.apiFetch(`${this.apiUrl('billing/subscription-plans')}`);
        if (!response.ok) return;
        this.subscriptionPlans = await response.json() || [];
        const gateways = this.planGateways();
        if (!gateways.includes(this.subGateway)) {
          this.subGateway = gateways[0] || '';
        }
        const plans = this.gatewayPlans();
        if (!plans.some((p) => p.id === this.planChoice)) {
          this.planChoice = plans.length ? plans[0].id : '';
        }
      } catch (error) {}
    },

    // Which gateways this server can charge through. Stripe and PayPal are
    // always offered (their options predate the endpoint); Square joins the
    // reload picker only when the server reports it configured.
    async loadPaymentMethods() {
      try {
        const response = await this.apiFetch(`${this.apiUrl('pub/billing/payment-methods')}`);
        if (!response.ok) return false;
        const data = await response.json();
        const methods = (data.methods || []).filter((method) => ['stripe', 'paypal', 'square'].includes(method));
        // Renewals offer only configured gateways; the reload form keeps its
        // existing Stripe/PayPal fallback independently.
        this.licenseRenewMethods = methods;
        if (!methods.includes(this.licenseRenewGateway)) this.licenseRenewGateway = methods[0] || '';
        this.reloadMethods = ['stripe', 'paypal'].concat(methods.includes('square') ? ['square'] : []);
        if (!this.reloadMethods.includes(this.reloadMethod)) {
          this.reloadMethod = this.reloadMethods[0];
        }
        this.update();
        return true;
      } catch (error) {}
      return false;
    },

    // Gateways that actually have plans, in the reload form's own order.
    planGateways() {
      return ['stripe', 'paypal', 'square'].filter((g) => this.subscriptionPlans.some((p) => p.gateway === g));
    },

    // The picked gateway's plans — what the Plan select shows.
    gatewayPlans() {
      return this.subscriptionPlans.filter((p) => p.gateway === this.subGateway);
    },

    // Switching gateway swaps the plan list, so the picked plan follows.
    pickSubGateway(gateway) {
      this.subGateway = gateway;
      const plans = this.gatewayPlans();
      this.planChoice = plans.length ? plans[0].id : '';
      this.update();
    },

    // "Account Reload · $1.00 / month" — the plan picker's one-line
    // description: the gateway-side name (Stripe product / PayPal plan),
    // then the price and cycle it fixes. The gateway itself is the other
    // select, so it is not repeated here.
    planLabel(p) {
      const price = this.formatAmount(p.amount) + ' / ' + p.interval;
      return p.name ? p.name + ' · ' + price : price;
    },

    async reload() {
      if (this.reloadLoading) return;
      const amount = parseFloat(this.reloadAmount);
      if (!amount || amount <= 0) {
        this.showToast('Enter a valid amount', 'danger');
        return;
      }
      this.reloadLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${this.apiUrl('billing/reload')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount, payment_method: this.reloadMethod }),
        });
        const data = await response.json();
        if (response.ok && data.url) {
          window.location.href = data.url;
        } else {
          this.showToast(data, 'danger');
          this.reloadLoading = false;
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
        this.reloadLoading = false;
      }
    },

    async loadSubscriptions() {
      try {
        const response = await this.apiFetch(`${this.apiUrl('billing/subscriptions')}`);
        if (response.ok) {
          this.subscriptions = await response.json() || [];
        }
      } catch (error) {}
    },

    // The Your-subscriptions list is tabbed: Active holds the ones still
    // reloading, Canceled keeps the stopped ones as history instead of
    // letting them vanish from the page.
    activeSubs() {
      return this.subscriptions.filter((s) => s.status !== 'canceled');
    },

    canceledSubs() {
      return this.subscriptions.filter((s) => s.status === 'canceled');
    },

    shownSubs() {
      return this.subsTab === 'canceled' ? this.canceledSubs() : this.activeSubs();
    },

    pickSubsTab(tab) {
      this.subsTab = tab;
      this.update();
    },

    // Starts a recurring reload on the picked plan. The plan carries the
    // price; nothing is typed. The gateway's own page is the confirmation
    // step: Stripe's checkout and PayPal's approval page both spell out the
    // recurring charge before anything is agreed to.
    async subscribe() {
      if (this.subscribeLoading) return;
      if (!this.subGateway || !this.planChoice) {
        this.showToast('Pick a subscription plan', 'danger');
        return;
      }
      this.subscribeLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${this.apiUrl('billing/subscribe')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_method: this.subGateway, plan_id: this.planChoice }),
        });
        const data = await response.json();
        if (response.ok && data.url) {
          window.location.href = data.url;
        } else {
          this.showToast(data, 'danger');
          this.subscribeLoading = false;
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
        this.subscribeLoading = false;
      }
    },

    async cancelSubscription(sub) {
      const cycle = { week: 'weekly', month: 'monthly', year: 'yearly' }[sub.billing_interval] || 'recurring';
      const confirmed = await this.showConfirm({
        title: 'Cancel Recurring Reload',
        message: 'Stop the ' + this.formatAmount(sub.amount) + ' ' + cycle + ' reload? Balance already credited stays yours.',
        action: 'Stop Reloads',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl('billing/subscription/cancel')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: sub.id }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadSubscriptions();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    viewOrderItems(order, source) {
      this.orderDetailTitle = 'Order ' + order.id.slice(0, 8);
      this.orderDetailItems = order.items || [];
      this.orderDetailOrder = order;
      this.orderDetailSource = source || '';
      this.update();
      this.querySelector('.order-detail-dialog').showModal();
    },

    canReturn() {
      var o = this.orderDetailOrder;
      return o && o.status !== 'refunded' && this.orderDetailSource === 'admin';
    },

    canRequestReturn() {
      var o = this.orderDetailOrder;
      return o && o.status !== 'refunded' && this.orderDetailSource === 'user';
    },

    returnFromDetail() {
      this.closeOrderDetail();
      this.openReturnDialog(this.orderDetailOrder);
    },

    requestReturnFromDetail() {
      this.closeOrderDetail();
      this.openReturnDialog(this.orderDetailOrder);
    },

    closeOrderDetail() {
      this.querySelector('.order-detail-dialog').close();
    },

    async loadMyReturns() {
      try {
        if (await this.fetchList('myReturns', `${this.apiUrl('billing/return-requests')}`)) {
          this.myReturnsLoaded = true;
        }
      } catch (e) {}
    },

    returnRequestSummary(items) {
      if (!items || !items.length) return '';
      return items.map(function(i) {
        return (i.product_name || i.order_item_id.slice(0, 8)) + ' x' + i.quantity;
      }).join(', ');
    },

    returnRequestTotal(items) {
      if (!items || !items.length) return 0;
      var total = 0;
      for (var i = 0; i < items.length; i++) {
        total += (items[i].price || 0) * (items[i].quantity || 0);
      }
      return total;
    },

    async cancelReturn(requestID) {
      const confirmed = await this.showConfirm({
        title: 'Cancel Return Request',
        message: 'Are you sure you want to cancel this return request?',
        action: 'Cancel Request',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl('billing/return-request/cancel?id=' + encodeURIComponent(requestID))}`, { method: 'POST' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadMyReturns();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },
};
